import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Activity, Trash2 } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { DatabaseSchema, HealthRiskSnapshot } from '../../types';

interface Props {
  database: DatabaseSchema | null;
  onAddHealthRiskSnapshot: (entry: HealthRiskSnapshot) => Promise<void>;
  onDeleteHealthRiskSnapshot: (id: string) => Promise<void>;
}

export const RiskScores: React.FC<Props> = ({ database, onAddHealthRiskSnapshot, onDeleteHealthRiskSnapshot }) => {
  const snapshots = [...(database?.healthRiskSnapshots ?? [])].sort((a, b) => b.date.localeCompare(a.date));
  const [date, setDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [qDiabetes, setQDiabetes] = useState('');
  const [qRisk, setQRisk] = useState('');
  const [notes, setNotes] = useState('');

  const handleSave = async () => {
    if (!qDiabetes && !qRisk) return;
    await onAddHealthRiskSnapshot({
      id: `risk-${Date.now()}`,
      date,
      qDiabetes: qDiabetes ? Number(qDiabetes) : undefined,
      qRisk3: qRisk ? Number(qRisk) : undefined,
      source: 'gp_screening',
      notes: notes || undefined,
    });
    setQDiabetes('');
    setQRisk('');
    setNotes('');
  };

  return (
    <Box>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
            <Activity size={22} />
            <Typography variant="h6" fontWeight={700}>Risk scores</Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            QDiabetes (10-yr type 2 risk %) and QRISK3 (10-yr cardiovascular risk %). Enter values your GP calculates.
          </Typography>

          <Stack spacing={1.5}>
            <TextField label="Date" type="date" size="small" value={date} onChange={(e) => setDate(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
            <Stack direction="row" spacing={1}>
              <TextField label="QDiabetes %" type="number" size="small" value={qDiabetes} onChange={(e) => setQDiabetes(e.target.value)} fullWidth />
              <TextField label="QRISK3 %" type="number" size="small" value={qRisk} onChange={(e) => setQRisk(e.target.value)} fullWidth />
            </Stack>
            <TextField label="Notes" size="small" value={notes} onChange={(e) => setNotes(e.target.value)} fullWidth />
            <Button variant="contained" onClick={handleSave} disabled={!qDiabetes && !qRisk}>Save snapshot</Button>
          </Stack>
        </CardContent>
      </Card>

      {snapshots.map((s) => (
        <Card key={s.id} sx={{ mb: 1.5 }}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>
                  {format(parseISO(s.date), 'd MMM yyyy')}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                  {s.source.replace('_', ' ')}
                </Typography>
              </Box>
              <IconButton size="small" onClick={() => onDeleteHealthRiskSnapshot(s.id)}>
                <Trash2 size={16} />
              </IconButton>
            </Stack>
            <Stack direction="row" spacing={3} sx={{ mt: 1.5 }}>
              {s.qDiabetes !== undefined && (
                <Box>
                  <Typography variant="caption" color="text.secondary">QDiabetes</Typography>
                  <Typography variant="h5" fontWeight={700}>{s.qDiabetes}%</Typography>
                </Box>
              )}
              {s.qRisk3 !== undefined && (
                <Box>
                  <Typography variant="caption" color="text.secondary">QRISK3</Typography>
                  <Typography variant="h5" fontWeight={700}>{s.qRisk3}%</Typography>
                </Box>
              )}
            </Stack>
            {s.notes && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{s.notes}</Typography>
            )}
          </CardContent>
        </Card>
      ))}
    </Box>
  );
};
